import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { BigButton } from '../components/BigButton';
import { capturePhoto, uploadPhoto } from '../lib/camera';
import { runSafeguardingGate } from '../lib/safeguarding';
import { telemetry } from '../lib/telemetry';
import { useWorksheet } from '../context/WorksheetContext';
import { useExamMode } from '../context/ExamModeContext';

// Step 1: add a worksheet photo - either take one now, or upload an existing image
// (gallery, scanner output, email attachment, screenshot, etc).
// Every image passes the safeguarding gate before it is stored anywhere or shown back.
// No body images allowed - hard rule from the spec.
export function CaptureScreen() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { setImage } = useWorksheet();
  const { active: examModeActive, logEvent } = useExamMode();

  async function handleSource(source: 'camera' | 'upload') {
    setError(null);
    setBusy(true);
    telemetry.track('worksheet_capture_started', { source });

    try {
      const dataUrl = source === 'camera' ? await capturePhoto() : await uploadPhoto();
      if (!dataUrl) {
        setBusy(false);
        return; // User cancelled the system picker.
      }

      const result = await runSafeguardingGate(dataUrl);

      if (!result.ok) {
        telemetry.track('worksheet_capture_rejected', { reason: result.reason, source });
        setError(friendlyMessage(result.reason));
        setBusy(false);
        return;
      }

      telemetry.track('worksheet_capture_accepted', { source });
      if (examModeActive) {
        logEvent(source === 'camera' ? 'worksheet_captured' : 'worksheet_uploaded');
      }
      setImage(dataUrl);
      navigate('/app/review');
    } catch (e) {
      console.error('Capture failed', e);
      setError(
        source === 'camera'
          ? 'Something went wrong with the camera. Try again, or use Upload from gallery instead.'
          : 'Something went wrong opening the picker. Try again.',
      );
      setBusy(false);
    }
  }

  return (
    <Screen>
      <div className="flex-1 flex flex-col gap-6 py-6">
        <header>
          <button
            onClick={() => navigate('/app')}
            className="text-ink/60 text-sm hover:text-ink transition"
            aria-label="Back to home"
          >
            ← Back
          </button>
        </header>

        <div className="flex-1 flex flex-col justify-center gap-6">
          <h2 className="text-2xl font-semibold text-center">Add a photo of your worksheet</h2>
          <p className="text-center text-sm text-ink/60 -mt-3">
            Worksheet, workbook page, scan or screenshot - all fine.
          </p>

          <div className="bg-white rounded-2xl border border-ink/10 p-6 leading-relaxed text-ink/70">
            <p className="mb-3 font-medium text-ink">Tips for a great result:</p>
            <ul className="space-y-2 text-sm">
              <li>Keep the page as flat as you can.</li>
              <li>Make sure the question text is in focus and readable.</li>
              <li>Good light helps. Daylight is best.</li>
              <li>Workbook pages with a binding edge are fine - one edge running off the photo is OK.</li>
              <li>Already have a scan or screenshot? Use Upload instead.</li>
            </ul>
          </div>

          {error && (
            <div
              role="alert"
              className="bg-warm border border-amber-300 text-ink rounded-xl p-4 text-sm leading-relaxed"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <BigButton onClick={() => handleSource('camera')} disabled={busy}>
              {busy ? 'Working...' : 'Take a photo'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => handleSource('upload')} disabled={busy}>
              Upload from gallery
            </BigButton>
            <button
              onClick={() => navigate('/app')}
              className="text-sm text-ink/55 hover:text-ink transition self-center mt-1"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Screen>
  );
}

function friendlyMessage(reason: string): string {
  switch (reason) {
    case 'face_detected':
      return 'That looks like a photo of a person. Answer Assist only works with pages of learning material. Try pointing the camera at the page instead, or upload a scan.';
    case 'too_much_skin':
      return 'Hmm, that does not look like a page of learning material. Try the photo again with the page filling more of the screen, or upload a scan if you have one.';
    case 'no_text_found':
      return 'I could not see any words on that image. Try again with the page filling more of the screen.';
    case 'low_quality':
      return 'The image is too blurry or dark to read. Find better light, hold the camera steady, or upload a clearer scan.';
    default:
      return 'Hmm, I could not read that image. Try again, or upload a clearer one.';
  }
}
